import type React from 'react';
import { useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import Button from '@polyai/ui/components/atoms/Button';

import type { DummyAssistant, ProjectWithMetadata } from 'api/types';
import FormProvider, { FormSelect } from '../../../components/molecules/Form';
import { type AgentFormData, agentSchema } from '../types';

interface AgentFormProps {
  projects: (ProjectWithMetadata | DummyAssistant)[];
  isLoadingProjects: boolean;
  onSubmit: (data: AgentFormData) => void;
  isSubmitting: boolean;
}

const AgentForm: React.FC<AgentFormProps> = ({
  projects,
  isLoadingProjects,
  onSubmit,
  isSubmitting,
}) => {
  const methods = useForm<AgentFormData>({
    resolver: zodResolver(agentSchema),
    defaultValues: {
      agent: undefined,
    },
  });

  const agentOptions = useMemo(() => {
    const projectOptions = projects.map(project => ({
      label: project.name || project.id,
      value: project.id,
    }));

    return projectOptions;
  }, [projects]);

  const { isValid } = methods.formState;

  return (
    <>
      <div>
        <h2 className="mb-sm1 text-header-large font-semibold text-primary">
          Select an agent
        </h2>
        <p className="text-body-regular text-secondary">
          Select the agent that you want to connect your OT account with
        </p>
      </div>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <div className="flex flex-col gap-sm4">
          <FormSelect
            className="text-body-regular"
            controlKey="agent"
            help="Select the agent you want to connect with"
            isDisabled={isLoadingProjects}
            label="Agent"
            options={agentOptions}
            placeholder={
              isLoadingProjects ? 'Loading agents...' : 'Select an agent...'
            }
          />

          <div>
            <Button
              disabled={isSubmitting || !isValid}
              label="Continue"
              type="submit"
              variant="PRIMARY"
              fullWidth
            />
          </div>
        </div>
      </FormProvider>
    </>
  );
};

export default AgentForm;
